import { statusKey, statusType } from "helpers/GeneralHelper";
import StatusMetadataBadge from "components/Common/StatusMetadataBadge";
import { updateJobRequired } from "model/JobRequiredModel";
import { addQuestionType, addSingleQuestion, getAllQuestionsUsingQuestionTypeId, getQuestionTypeList, updateQuestionType, updateSingleQuestion } from "model/QuestionTypeModel";
import React, { useState } from "react";
import {
  Card,
  CardBody,
  Table,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Form,
  FormGroup,
  UncontrolledTooltip,
} from "reactstrap";
import Spinners from "./Spinner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";


const isQuestionTypeStatusColumn = (accessor) => statusKey(accessor) === "status";

const getQuestionTypeColumnClassName = (accessor, { isHeader = false } = {}) => {
  const classNames = [isHeader ? "text-nowrap" : "align-middle"];
  if (isQuestionTypeStatusColumn(accessor)) {
    classNames.push("text-center", "common-table-status-col");
  } else if (accessor === "questionTypeId") {
    classNames.push("text-center");
  }
  return classNames.join(" ");
};

const ExpandableQuestionTable = ({
  columns,
  data,
  onEdit,
  isEditAccess,
  isPagination,
  fetchQuestionTypeList,
  pageName = "",
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(null);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  /* ================= MODAL STATE ================= */
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    detailsName: "",
    expectedAnswer: "",
    isMandatory: false,
    detailsStatus: false,
  });
  const [formKey, setFormKey] = useState(0);
  const [itemId, setItemId] = useState(null); // ✅ important
  const [allQuestions, setAllQuestions] = useState({}); // ✅ important
  const [questionLoading, setQuestionLoading] = useState(false); // ✅ important

  // Column modal
  const [showColumnModal, setShowColumnModal] = useState(false);
  const toggleColumnModal = () => setShowColumnModal(!showColumnModal);
  const [columnOrder, setColumnOrder] = useState(columns.map((col) => col?.accessor));
  const [questionTypeId, setQuestionTypeId] = useState(null); // ✅ important


  const resolveColumnValue = (item, accessor) => {
    const value = item?.[accessor];

    switch (accessor) {
      case "status":
      case "jobCategoryStatus":
      case "jobRequireStatus":
        return value ?? "";

      case "quotaIsOn":
        return value ? "Yes" : "No";

      case "forClient":
      case "forJob":
        return value == "1" ? "Yes" : "No";

      case "quota":
        return value ? "Yes" : "No";

      case "incentiveType":
        return value ?? "";

      case "video":
      case "videoLink":
        return value || "";

      case "is_allowed":
        return value ? "Yes" : "No";

      case "notes":
      case "fedex_id":
        return value || "";

      default:
        return value ?? "";
    }
  };



  const toggleModal = () => setModalOpen(!modalOpen);

  const openAddModal = (parent = null) => {
    setEditMode(false);
    setCurrentItem(parent);
    setFormData({ name: "", detailsName: "", isMandatory: false, detailsStatus: false });
    setModalOpen(true);
  };

  const openEditModal = (data) => {
    setEditMode(true);
    setCurrentItem(data);
    setItemId(data?.questionTypeDetailsId)
    setFormData({
      detailsName: data?.detailsName || "",
      expectedAnswer: data?.expectedAnswer || "",
      isMandatory: data?.isMandatory,
      detailsStatus: data?.detailsStatus === 'active'
    });
    setModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const params = {
      ...formData,
      detailsStatus: formData?.detailsStatus ? "active" : "deactive",
      questionTypeId: questionTypeId || null
    };
    let response;
    if (itemId) {
      response = await updateSingleQuestion(itemId, params);
    } else {
      response = await addSingleQuestion(params);
    }
    if (response?.success) {
      setExpandedRows(questionTypeId); // ✅ ensure it's open
      toast.success(response?.message);
      // 🔁 re-fetch questions for the SAME accordion
      if (questionTypeId) {
        await fetchAllQuestionsUsingQuestionsTypeId(questionTypeId);
        setExpandedRows(questionTypeId); // ✅ ensure it's open
      }
      closeModal();
      // fetchQuestionTypeList();
    } else {
      toast.error(response?.message);
    }
  };


  /* ================= SORTING ================= */
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (accessor) => {
    setSortConfig((prev) => {
      if (prev.key === accessor) {
        return { key: accessor, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key: accessor, direction: "asc" };
    });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [data, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = isPagination ? sortedData.slice(startIndex, startIndex + rowsPerPage) : sortedData;

  // const toggleRow = (questionTypeId) => {
  //   setExpandedRows((prev) => ({ ...prev, [questionTypeId]: !prev[questionTypeId] }));
  // };
  const toggleRow = (questionTypeId) => {
    setExpandedRows(prev =>
      prev === questionTypeId ? null : questionTypeId
    );
  };


  /* ================= ICONS ================= */
  const IconEdit = (
    <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
      <path d="M3 17.25V21h3.75l11-11.03-3.75-3.75L3 17.25z" />
    </svg>
  );

  const SortIcon = ({ column }) =>
    sortConfig.key === column ? (sortConfig.direction === "asc" ? <i className="fas fa-sort-up mw-2"></i> : <i className="fas fa-sort-down mw-2"></i>) : null;

  const closeModal = () => {
    setModalOpen(false)
  };

  // const openAccordionAndFetchAllQuestions = (item) => {
  //   if (item?.questionTypeId) {
  //     setQuestionTypeId(item?.questionTypeId)
  //     fetchAllQuestionsUsingQuestionsTypeId(item?.questionTypeId);
  //   }

  // }

  const openAccordionAndFetchAllQuestions = async (item) => {
    if (!item?.questionTypeId) return;

    const id = item?.questionTypeId;

    setExpandedRows(id);          // ✅ open this accordion
    setQuestionTypeId(id);         // keep reference
    await fetchAllQuestionsUsingQuestionsTypeId(id);
  };

  const fetchAllQuestionsUsingQuestionsTypeId = async (questionTypeId) => {
    setQuestionLoading(true);
    let response = await getAllQuestionsUsingQuestionTypeId({ Id: questionTypeId });
    let questionsData = response?.data;
    if (response?.success) {
      // setAllQuestions(questionsData);
      setAllQuestions(prev => ({
        ...prev,
        [questionTypeId]: questionsData || []
      }));
      setQuestionLoading(false);
    } else {
      setAllQuestions([]);
      setQuestionLoading(false);
    }
  };
  const getVisibleColumns = () =>
    columnOrder
      .map((accessor) => columns.find((c) => c.accessor === accessor))
      .filter((col) => visibleColumns[col?.accessor]);
  const [visibleColumns, setVisibleColumns] = useState(
    columns.reduce((acc, col) => ({ ...acc, [col?.accessor]: true }), {})
  );

  const handleColumnToggle = (accessor) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };



  const getTableData = () =>
    sortedData.map((row) =>
      getVisibleColumns().map((col) =>
        resolveColumnValue(row, col.accessor)
      )
    );


  const handleCopy = async () => {
    const headers = getVisibleColumns().map((c) => c.Header).join("\t");
    const rows = getTableData()
      .map((r) => r.join("\t"))
      .join("\n");

    await navigator.clipboard.writeText(`${headers}\n${rows}`);
  };
  const handleCSV = () => {
    const headers = getVisibleColumns().map((c) => c.Header);
    const rows = getTableData();

    const csvContent = [headers, ...rows]
      .map((e) => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Question Type  - (DEV) Ancile Hiring Tools.csv";
    link.click();
  };
  // const handleExcel = () => {
  //   const wsData = [
  //     getVisibleColumns().map((c) => c.Header),
  //     ...getTableData(),
  //   ];

  //   const worksheet = XLSX.utils.aoa_to_sheet(wsData);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

  //   XLSX.writeFile(workbook, "table.xlsx");
  // };

  const handleExcel = () => {
    const headers = getVisibleColumns().map(col => col.Header);
    const rows = getTableData();

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        const cell = worksheet[cellRef];
        if (!cell) continue;

        cell.s = {
          font: {
            bold: r === 0, // ✅ ONLY header row bold
            name: "Calibri",
            sz: 15,
          },
          alignment: {
            vertical: "center",
            horizontal: "left",
            wrapText: true,
          },
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          },
        };
      }
    }

    worksheet["!cols"] = headers.map((h) => {
      if (h.toLowerCase() === "id") return { wch: 6 };
      if (h.toLowerCase() === "video") return { wch: 80 };
      return { wch: 30 };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Data");

    XLSX.writeFile(wb, "Question Type  - (DEV) Ancile Hiring Tools.xlsx");
  };



  const handlePrint = () => {
    const headers = getVisibleColumns().map((c) => `<th>${c.Header}</th>`).join("");
    const rows = getTableData()
      .map(
        (r) =>
          `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`
      )
      .join("");

    const win = window.open("", "", "width=900,height=700");
    win.document.write(`
          <html>
            <head>
              <title>Print</title>
              <style>
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
              </style>
            </head>
            <body>
              <table>
                <thead><tr>${headers}</tr></thead>
                <tbody>${rows}</tbody>
              </table>
            </body>
          </html>
        `);
    win.document.close();
    win.print();
  };
  const handlePDF = () => {
    const doc = new jsPDF("l", "pt", "a4"); // landscape like screenshot

    const columns = getVisibleColumns().map(col => col.Header);
    const rows = getTableData();

    autoTable(doc, {
      head: [columns],
      body: rows,

      startY: 40,
      theme: "striped",

      styles: {
        fontSize: 9,
        cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
        textColor: "#000",
        lineColor: "#e5e7eb",
        lineWidth: 0.5,
        valign: "middle",
        halign: "left",
      },

      headStyles: {
        fillColor: "#2f3e4e",   // dark blue header
        textColor: "#ffffff",
        fontStyle: "bold",
        fontSize: 10,
        halign: "left",
        valign: "middle",
      },

      alternateRowStyles: {
        fillColor: "#f3f4f6", // light gray stripe
      },

      bodyStyles: {
        fillColor: "#ffffff",
      },

      didParseCell: function (data) {
        // Prevent long text overflow like emails
        data.cell.styles.cellWidth = "wrap";
      },

      margin: { left: 20, right: 20 },
    });

    doc.save("Question Type  - (DEV) Ancile Hiring Tools.pdf");
  };

  return (
    <Card className="shadow-sm border-0">
      <CardBody className="p-0">

        {/* Toolbar */}
        <div className="d-flex align-items-center justify-content-between px-2 py-2 pad-btm" style={{ backgroundColor: "#f8fafc" }}>
          <div className="d-flex align-items-center gap-2">
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline table-action-icon" onClick={toggleColumnModal}>
              <i className="fa fa-columns" aria-hidden="true"></i>
              COLUMNS
            </Button>
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-csv" onClick={handleCopy}>
              <i className="fa fa-copy" aria-hidden="true"></i>
              COPY
            </Button>
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-csv" onClick={handleCSV}>
              <i className="fas fa-file-csv"></i>
              CSV
            </Button>
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-excel" onClick={handleExcel}>
              <i className="fas fa-file-excel"></i>
              EXCEL
            </Button>
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-print" onClick={handlePrint}>
              <i className="fa fa-print" aria-hidden="true"></i>
              PRINT
            </Button>
            <Button color="link" className="text-dark d-flex align-items-center gap-1 p-0 fw-semibold no-underline icon-pdf" onClick={handlePDF}>
              <i className="fa fa-file-pdf" aria-hidden="true"></i>
              PDF
            </Button>
          </div>
        </div>
        {/* Table */}
        <div className="table-responsive" style={{ backgroundColor: "#f8fafc" }}>
          <Table hover borderless className="align-middle mb-0 custom-table spaced-table">
            <thead style={{ backgroundColor: "#f8fafc" }}>
              <tr style={{ color: "#374151", fontWeight: 600, fontSize: "14px" }} className="mb-2">
                {/* <th style={{ width: "40px" }}></th> */}
                {getVisibleColumns().map((col) => (
                  <th
                    key={col.accessor}
                    onClick={() => handleSort(col.accessor)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                    className={getQuestionTypeColumnClassName(col.accessor, { isHeader: true })}
                  >
                    {col.Header}
                    <SortIcon column={col.accessor} />
                  </th>
                ))}

                <th
                  className="align-middle text-center team-table-actions-cell"
                  style={{ width: "90px", minWidth: "90px" }}
                />
              </tr>
            </thead>

            <tbody style={{ backgroundColor: "#ffffff" }}>
              {paginatedData?.map((item, idx) => (
                <React.Fragment key={idx}>
                  {/* Parent Row */}
                  <tr>
                    {/* <td className="text-center cursor-pointer">
                      {expandedRows === item?.questionTypeId ? (
                        <i
                          className="fas fa-minus text-danger"
                          onClick={() => toggleRow(item.questionTypeId)}
                        />
                      ) : (
                        <i
                          className="fas fa-plus text-success"
                          onClick={() => openAccordionAndFetchAllQuestions(item)}
                        />
                      )}

                    </td> */}
                    {getVisibleColumns().map((col) => (
                      <td key={col.accessor} className={getQuestionTypeColumnClassName(col.accessor)}>
                        {col.accessor === "incentive" ? (
                          <span
                            className="px-2 py-1 fw-semibold accessor-incentive"
                          >
                            {item[col.accessor]}
                          </span>
                        ) : (statusKey(col.accessor) === "status") ? (
                          <StatusMetadataBadge
                            accessor={col.accessor}
                            pageName={pageName}
                            value={item[col?.accessor]}
                            fallbackClassName={statusType(
                              String(item[col?.accessor] ?? "")
                                .trim()
                                .toLowerCase()
                            )}
                          />
                        ) : (
                          item[col.accessor]
                        )}
                      </td>
                    ))}

                    <td
                      className="align-middle text-center team-table-actions-cell px-1 py-2"
                      style={{ minWidth: "88px" }}
                    >
                      <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                        {isEditAccess && (
                          <>
                            <span
                              className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center"
                              onClick={() => onEdit?.(item)}
                              id={`question-type-edit-${item?.questionTypeId || item?.id || idx}`}
                              role="button"
                            >
                              <i
                                className="mdi mdi-pencil font-size-18 text-success"
                                aria-hidden
                              />
                            </span>
                            <UncontrolledTooltip
                              target={`question-type-edit-${item?.questionTypeId || item?.id || idx}`}
                              placement="top"
                            >
                              Edit
                            </UncontrolledTooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Content */}
                  {/* {(expandedRows === item.questionTypeId &&
                    allQuestions[item.questionTypeId]?.length) ? (
                    <tr>
                      <td></td>
                      <td colSpan={columns.length + 1}>
                        <div className="p-1">
                          <h6 className="fw-bold mb-2 d-flex justify-content-between align-items-center">
                            {item?.questionTypeName} - Details
                            <Button
                              color="primary"
                              onClick={() => openAddModal(item)}
                            >
                              <i className="fa fa-plus fs-6" /> Add
                            </Button>
                          </h6>

                          <Table bordered size="sm" className="mb-2 question-table">
                            <thead>
                              <tr>
                                <th>#SN</th>
                                <th>Question</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {allQuestions[item.questionTypeId]?.map((question, i) => (
                                <tr key={i}>
                                  <td className="text-center">{i + 1}</td>
                                  <td className="question-td">{question?.detailsName}</td>
                                  <td><span className="text-muted" style={{ cursor: "pointer" }} onClick={() =>
                                    openEditModal(question)
                                  }>
                                    <i className="fas fa-edit" />
                                  </span></td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </td>
                    </tr>
                  )
                    :
                    null
                  } */}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        {isPagination ?
          <div
            className="d-flex justify-content-end align-items-center px-3 py-2 border-top bg-white"
            style={{ borderTop: "1px solid #e9ecef" }}
          >
            <div className="d-flex align-items-center gap-2 me-3">
              <span className="text-muted small">Rows per page:</span>
              <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle caret color="light" size="sm" className="border bg-white">
                  {rowsPerPage}
                </DropdownToggle>
                <DropdownMenu>
                  {[5, 10, 20].map((num) => (
                    <DropdownItem key={num} onClick={() => setRowsPerPage(num)}>
                      {num}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
            <span className="text-muted small me-3">
              {startIndex + 1}–
              {Math.min(startIndex + rowsPerPage, data.length)} of {data.length}
            </span>
            <div className="d-flex align-items-center gap-1">
              <Button
                color="link"
                className="text-muted p-0"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor">
                  <path d="M15.41 16.58L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
                </svg>
              </Button>
              <Button
                color="link"
                className="text-muted p-0"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor">
                  <path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                </svg>
              </Button>
            </div>
          </div> : null
        }
        {/* ================= MODAL ================= */}

        <Modal
          isOpen={modalOpen}
          toggle={toggleModal}
          backdrop="static"
          className="modal-full-height"
          centered
        >
          <ModalBody
            key={formKey}
            className="p-4"
            style={{ maxHeight: "95vh", overflowY: "auto" }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">{editMode ? "Edit " : "Add "}
                Question</h5>
              <Button close onClick={closeModal} />
            </div>
            <hr />

            <Form onSubmit={handleSubmit}>
              {/* Name */}
              <FormGroup>
                <Label>Question</Label>
                <Input
                  type="text"
                  name="detailsName"
                  defaultValue={formData?.detailsName}
                  onChange={handleChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Expected Answer</Label>
                <Input
                  type="text"
                  name="expectedAnswer"
                  defaultValue={formData?.expectedAnswer}
                  onChange={handleChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Is Mandatory</Label>
                <div className="input-box d-flex align-items-center justify-content-between">
                  <span className="text-muted"></span>
                  <div>
                    <Input
                      type="radio"
                      name="isMandatory"
                      defaultChecked={formData?.isMandatory}
                      onChange={() =>
                        setFormData((p) => ({ ...p, isMandatory: true }))
                      }
                    />{" "}
                    Yes
                    <Input
                      type="radio"
                      name="isMandatory"
                      defaultChecked={!formData?.isMandatory}
                      onChange={() =>
                        setFormData((p) => ({ ...p, isMandatory: false }))
                      }
                      className="ms-3"
                    />{" "}
                    No
                  </div>
                </div>
              </FormGroup>

              {/* Status */}
              <FormGroup>
                <Label>Status</Label>
                <div className="input-box d-flex align-items-center justify-content-between">
                  <span className="text-muted">Type</span>
                  <div className="d-flex align-items-center">
                    <strong className="me-2">
                      {formData?.detailsStatus ? "Active" : "Inactive"}
                    </strong>
                    <div className="form-switch">
                      <Input
                        type="switch"
                        name="detailsStatus"
                        defaultChecked={formData?.detailsStatus}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </FormGroup>

              <div className="text-end mt-3">
                <Button color="primary" type="submit">
                  {editMode ? "Update" : "Save"}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        {/* Column Modal */}
        <Modal size="md" isOpen={showColumnModal} toggle={toggleColumnModal} backdrop="static" centered={true} className="modal-full-height"
        >
          <ModalHeader toggle={toggleColumnModal}>Manage Columns</ModalHeader>
          <ModalBody className="p-4 modal-height-fixed">
            {columns?.map((col) => (
              <div key={col?.accessor} className="d-flex align-items-center mb-2">
                <input
                  type="checkbox"
                  className="form-check-input me-2"
                  defaultChecked={visibleColumns[col?.accessor]}
                  onChange={() => handleColumnToggle(col?.accessor)}
                />
                <label className="form-check-label">{col?.Header}</label>
              </div>
            ))}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleColumnModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default ExpandableQuestionTable;
